'use client';

import { FloatingArrow, useMergeRefs } from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { PopoverFloatingContext } from './context/popover-floating.context';
import { PopoverRootContext } from './context/popover-root.context';
import { popoverArrowRecipe } from './styles/popover-arrow.styles';
import type { PopoverArrowPropsInterface } from './types/popover-arrow.types';

/**
 * @since 10.0.0
 */
export const PopoverArrow = forwardRef<SVGSVGElement, PopoverArrowPropsInterface>(
  (props: PopoverArrowPropsInterface, forwardedRef) => {
    const { popover, modal, tone } = useContext(PopoverRootContext);
    const floating = useContext(PopoverFloatingContext);
    const { className, css: cssProp, width = 24, height = 14, tipRadius = 1, ...rest } = props;

    // Extract placement side and alignment from Floating UI placement
    const placementSide = popover.arrowSide;
    const placementAlignment = popover.arrowAlignment || 'start';

    const styles = css(
      popoverArrowRecipe.raw({
        modal,
        tone,
        placement: placementSide,
        alignment: placementAlignment,
      }),
      cssProp,
    );

    const mergedRefs = useMergeRefs([popover.arrowRef, forwardedRef]);

    return (
      <FloatingArrow
        ref={mergedRefs}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Popover.Arrow"
        className={cx('c11n', styles, className)}
        width={width}
        height={height}
        tipRadius={tipRadius}
        context={floating.context}
        {...rest}
      />
    );
  },
);

PopoverArrow.displayName = 'Popover.Arrow';
